import { ParamMap } from '@angular/router';

import { SectorTripNumber, ExchangeBookPayload } from '@user-history/entities/user-history-item';
import { environment } from '@environment';
import { NewFlightItemVm } from '@app/entities/flight-results';
import { NewAirSearchPayload, RequestSearchPayload } from '@flight-results/entities/request-search.payload';
import { AVIANCA_AIR_CODE } from '@shared/luggage/constants/airline-code.constants';
import { getBaseRequestSettings } from '@app/mappers/reguest-settings.mapper';
import { AirSearchPayloadTrips } from '@flight-results/entities/airSearchPayload';

export type PayloadExchange = Readonly<{
  itinerary_id: string,
  reservation_id: string,
  departure: string,
  arrival: string,
  arrival_type?: string,
  departure_sec_number: string,
  departure_sector_change: string,
  departure_date: string,
  departure_type?: string,
  arrival_sec_number?: string,
  arrival_sector_change?: string,
  arrival_date?: string,
  baggage_included: string,
  original_departure_date: string,
  original_arrival_date: string,
  original_route: string,
  adults: string,
  children: string,
  infants: string
}>;

export type ExchangeBookFullPayload = Readonly<{
  exchangePayload: ExchangeBookPayload,
  freeOfCharge: boolean,
  residualFlow: boolean,
  isMobile: boolean,
  selectedFlights: NewFlightItemVm[],
  exchangeValue: number,
  residualValue: number,
}>;

export function mapExchangePayload(
  payload: PayloadExchange, isMobile: boolean, disabledSector: AirSearchPayloadTrips[] = []
): RequestSearchPayload {
  const trips = getTrips(payload);  
  return {
    TravelItineraryId: payload.itinerary_id,
    AirReservationId: payload.reservation_id,
    Trips: trips,
    Adults: payload.adults,
    Children: payload.children,
    Infants: payload.infants,
    RequestSettings: getBaseRequestSettings(isMobile),
    DiscardPendingReservations: true,
    CurrencyCode: environment.countryInfo.currency_code,
    exchangeDisabledSector: disabledSector
  };
}

function getTrips(payload: PayloadExchange): AirSearchPayloadTrips[] {
  return payload.arrival_sec_number ? [
    {
      SectorNumber: SectorTripNumber.DEPARTURE,
      DeparturePoint: payload.departure,
      ArrivalPoint: payload.arrival,
      DepartureDate: payload.departure_date,
      DeparturePointType: payload.departure_type,
      ArrivalPointType: payload.arrival_type,
      SectorChange: payload.departure_sector_change ? JSON.parse(payload.departure_sector_change) : false
    },
    {
      SectorNumber: SectorTripNumber.RETURN,
      DeparturePoint: payload.arrival,
      ArrivalPoint: payload.departure,
      DepartureDate: payload.arrival_date,
      DeparturePointType: payload.arrival_type,
      ArrivalPointType: payload.departure_type,
      SectorChange: payload.arrival_sector_change ? JSON.parse(payload.arrival_sector_change) : false
    }
  ] : [
    {
      SectorNumber: Number(payload.departure_sec_number),
      DeparturePoint: payload.departure,
      ArrivalPoint: payload.arrival,
      DepartureDate: payload.departure_date,
      DeparturePointType: payload.departure_type,
      ArrivalPointType: payload.arrival_type,
      SectorChange: payload.departure_sector_change ? JSON.parse(payload.departure_sector_change) : false
    }
  ];
}

export function getExchangeBookPayload(
  selectedFlights: NewFlightItemVm[], itineraryPrice: string,
  isMobile: boolean, payload: NewAirSearchPayload, params: ParamMap
): ExchangeBookFullPayload {
  const price = itineraryPrice ? `${itineraryPrice}` : '';
  const priceMatch = price.match(/\d/g);
  const exchangeValue = priceMatch ? Number(priceMatch.join('')) : 0;
  const freeOfCharge = exchangeValue <= 0;
  const residualValue = selectedFlights[0]?.TotalResidualValue || 0;
  const residualFlow = residualValue > 0 && !airlineWithAutomaticFlow(selectedFlights[0]?.AirlineCode);
  return {
    isMobile, freeOfCharge, residualFlow, selectedFlights, residualValue, exchangeValue,
    exchangePayload: {
      OptionId: selectedFlights[0].OptionId,
      SearchId: payload?.Results?.SearchId, 
      SessionToken: payload.SessionToken, 
      ContactEmail: '',
      ContactPhone: '',
      ContextApp: '',
      TravelItineraryId: params.get('itinerary_id'), AirReservationId: params.get('reservation_id')
    }
  };
}

function airlineWithAutomaticFlow(airline: string): boolean {
  return AVIANCA_AIR_CODE === airline;
}
